import React from 'react';
import RetroHitCounter from 'react-retro-hit-counter';
import { styled } from '@linaria/react';

import useToggle from '@/hooks/use-toggle';
import useSound from '@/hooks/use-sound';

const HitCounter = ({ hits }: { hits: number }) => {
  const [isOn, toggleOn] = useToggle(false);

  const [playPress, { stop: pausePress }] = useSound(
    '/sounds/pop-down.mp3',
    {
      volume: 0.25,
    }
  );
  const [playOn] = useSound('/sounds/pop-up-on.mp3', {
    volume: 0.25,
  });
  const [playOff] = useSound('/sounds/pop-up-off.mp3', {
    volume: 0.25,
  });

  function toggleCounter() {
    pausePress();

    if (isOn) {
      playOff();
    } else {
      playOn();
    }

    toggleOn();
  }

  return (
    <Wrapper>
      <Button
        onMouseDown={() => playPress()}
        onMouseUp={toggleCounter}
        onKeyDown={(ev) => {
          if (ev.key === 'Enter') {
            toggleCounter();
          }
        }}
        data-is-on={String(isOn)}
        aria-label={`Retro-style hit counter, showing that ${hits} ${
          hits === 1 ? 'person has' : 'people have'
        } visited this page.`}
      >
        <RetroHitCounter
          hits={hits}
          withBorder={isOn}
          withGlow={isOn}
          minLength={6}
          size={22}
          padding={3}
          digitSpacing={2}
          segmentThickness={2}
          segmentSpacing={0.325}
          segmentActiveColor={
            isOn ? '#76FF03' : 'var(--hit-counter-active-segment)'
          }
          segmentInactiveColor={
            isOn ? '#315324' : 'var(--hit-counter-inactive-segment)'
          }
          backgroundColor={
            isOn ? '#222222' : 'var(--hit-counter-background)'
          }
          borderThickness={5}
          glowStrength={0.5}
        />
      </Button>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  --hit-counter-background: var(--syntax-bg);
  --hit-counter-active-segment: hsl(225deg 27% 40%);
  --hit-counter-inactive-segment: hsl(213deg 40% 87%);
  position: relative;
  /* Bafflingly, the hit counter library sets 'z-index: 2', which causes it to float above the header. Flatten it: */
  isolation: isolate;
  height: 38px;

  html[data-color-mode='dark'] & {
    --hit-counter-background: var(--color-gray-100);
    --hit-counter-active-segment: hsl(210deg 18% 70%);
    --hit-counter-inactive-segment: hsl(213deg 30% 20%);
  }
`;

const Button = styled.button`
  position: absolute;
  top: 0;
  right: -8px;
  border-radius: 2px;

  & > div {
    display: block !important;
  }

  & svg {
    max-width: revert !important;
  }

  & canvas {
    max-width: revert !important;
  }

  &[data-is-on='false'] > div {
    border: 5px solid var(--hit-counter-background);
  }
`;

export default HitCounter;
